
import { supabase } from '@/integrations/supabase/client';

export type CheckStatus = "pending" | "success" | "error" | "warning";

export interface AuthCheckResult {
  name: string;
  status: CheckStatus;
  message: string;
}

export async function checkAuthConfig(): Promise<AuthCheckResult[]> {
  const results: AuthCheckResult[] = [];
  
  // Check Supabase configuration
  try {
    // Make a simple query to check if Supabase is properly configured
    const { data, error } = await supabase.from('profiles').select('count', { count: 'exact' }).limit(1);
    
    if (error) {
      results.push({
        name: 'Supabase Konfiguration',
        status: 'error',
        message: `Fehler bei der Verbindung: ${error.message}`
      });
    } else {
      results.push({
        name: 'Supabase Konfiguration',
        status: 'success',
        message: 'Verbindung zu Supabase erfolgreich hergestellt'
      });
    }
  } catch (error: any) {
    results.push({
      name: 'Supabase Konfiguration',
      status: 'error',
      message: `Unerwarteter Fehler: ${error.message}`
    });
  }
  
  // Check auth settings
  try {
    // Check if auth functions are available
    if (typeof supabase.auth.signUp === 'function' && 
        typeof supabase.auth.signInWithPassword === 'function' && 
        typeof supabase.auth.signOut === 'function') {
      results.push({
        name: 'Auth Basisfunktionen',
        status: 'success',
        message: 'Basis Auth-Funktionen sind verfügbar'
      });
    } else {
      results.push({
        name: 'Auth Basisfunktionen',
        status: 'error',
        message: 'Eine oder mehrere Basis Auth-Funktionen fehlen'
      });
    }
  } catch (error: any) {
    results.push({
      name: 'Auth Basisfunktionen',
      status: 'error',
      message: `Unerwarteter Fehler: ${error.message}`
    });
  }
  
  // Check Magic Link
  try {
    if (typeof supabase.auth.signInWithOtp === 'function') {
      results.push({
        name: 'Magic Link',
        status: 'success',
        message: 'Magic Link Funktion ist verfügbar'
      });
    } else {
      results.push({
        name: 'Magic Link',
        status: 'error',
        message: 'Magic Link Funktion ist nicht verfügbar'
      });
    }
  } catch (error: any) {
    results.push({
      name: 'Magic Link',
      status: 'error',
      message: `Unerwarteter Fehler: ${error.message}`
    });
  }
  
  // Check Password Reset
  try {
    if (typeof supabase.auth.resetPasswordForEmail === 'function') {
      results.push({
        name: 'Passwort Reset',
        status: 'success',
        message: 'Passwort Reset Funktion ist verfügbar'
      });
    } else {
      results.push({
        name: 'Passwort Reset',
        status: 'error',
        message: 'Passwort Reset Funktion ist nicht verfügbar'
      });
    }
  } catch (error: any) {
    results.push({
      name: 'Passwort Reset',
      status: 'error',
      message: `Unerwarteter Fehler: ${error.message}`
    });
  }
  
  // Check OAuth
  try {
    if (typeof supabase.auth.signInWithOAuth === 'function') {
      results.push({
        name: 'OAuth',
        status: 'success',
        message: 'OAuth Funktion ist verfügbar'
      });
    } else {
      results.push({
        name: 'OAuth',
        status: 'error',
        message: 'OAuth Funktion ist nicht verfügbar'
      });
    }
  } catch (error: any) {
    results.push({
      name: 'OAuth',
      status: 'error',
      message: `Unerwarteter Fehler: ${error.message}`
    });
  }
  
  // Check environment variables
  results.push({
    name: 'Umgebungsvariablen',
    status: window.location.hostname.includes('localhost') ? 'warning' : 'success',
    message: window.location.hostname.includes('localhost') 
      ? 'Lokale Umgebung erkannt - stelle sicher, dass alle benötigten Umgebungsvariablen gesetzt sind'
      : 'Produktionsumgebung erkannt'
  });
  
  return results;
}

export function getOverallAuthStatus(results: AuthCheckResult[]): CheckStatus {
  if (results.some(r => r.status === 'error')) {
    return 'error';
  }
  if (results.some(r => r.status === 'warning')) {
    return 'warning';
  }
  return 'success';
}

export function getAuthLaunchReadiness(status: CheckStatus): string {
  switch (status) {
    case 'success':
      return 'Das Auth-System ist bereit für den Launch!';
    case 'warning':
      return 'Das Auth-System funktioniert, hat aber einige Warnungen die überprüft werden sollten.';
    case 'error':
      return 'Das Auth-System hat wichtige Fehler, die behoben werden müssen.';
    default:
      return 'Die Überprüfung des Auth-Systems steht noch aus.';
  }
}
